/**
 * Helpers do AdminLayout extraídos pra arquivo separado (BUG-002/003/006).
 *
 * Razão: react-refresh rule exige que componentes JSX estejam em arquivo
 * separado de helpers puros + facilita testes unitários sem render.
 */

/**
 * Rotas de MÓDULO (página com identidade própria). Quando pathname matchea
 * um destes prefixos, AdminLayout pula o wrap "Configurações" header +
 * group nav e renderiza só `<AppShell><Outlet /></AppShell>`.
 *
 * NÃO REVERTER: pra adicionar nova rota de módulo na sidebar, listar aqui
 * tbm pra evitar regressão BUG-002/003/006.
 */
export const MODULE_ROUTE_PREFIXES: ReadonlyArray<string> = [
  '/admin/financeiro/',
  '/admin/projetos', // pega /admin/projetos AND /admin/projetos/*
  '/admin/clientes-v2', // pega /admin/clientes-v2 AND /admin/clientes-v2/*
  // CRM v2 (COMERCIAL-REBUILD): as 4 telas do Comercial novo têm identidade
  // própria. A config (/admin/comercial/crm) NÃO está aqui de propósito —
  // ela vive dentro de Configurações (grupo Operação).
  '/admin/comercial/leads',
  '/admin/comercial/funil',
  '/admin/comercial/indicadores',
  '/admin/comercial/metas',
  // F3 (rodada COMERCIAL-AJUSTES): o submenu Orçamentos nasceu FORA desta
  // lista e renderizava com a casca de Configurações por cima.
  '/admin/comercial/orcamentos',
];

// PERMISSOES-ACESSO (P3, 2026-07-30): `COMERCIAL_COLAB_ROUTE_PREFIXES`,
// `COMERCIAL_ORCAMENTISTA_ROUTE_PREFIXES`, `isComercialColabRoute` e
// `isComercialOrcamentistaRoute` foram REMOVIDOS daqui.
//
// Eram lista fixa de rotas decidindo acesso: liberar uma tela pra alguém exigia
// editar código e fazer deploy. O acesso agora se decide na tela de Permissões
// (perfil + departamento + exceção por pessoa, tabelas `acesso_tela`/
// `acesso_regra` da migration 419). O que sobrou é só tradução rota → chave, e
// mora em `@/lib/acesso-rotas` — de propósito FORA deste arquivo, pra ninguém
// confundir "qual tela é esta URL" com "quem pode abrir".
//
// O comportamento de hoje foi preservado no SEED da migration: departamento
// `comercial` recebeu funil/leads/orçamentos/indicadores e `orcamentista`
// recebeu funil/orçamentos/indicadores (sem Leads) — exatamente o que estas
// listas faziam.

/**
 * `/admin/configuracoes` e `/admin/configuracoes/<secao>` — a Configurações NOVA.
 *
 * G-2: ela traz o próprio menu lateral de seções. Deixar a barra de abas antiga
 * ("Pessoas · Cadastros · Clientes · Operação · Integrações · Sistema") por cima
 * dá **duas navegações para o mesmo assunto na mesma tela** — exatamente o que a
 * tela nova existe pra acabar. E elas **discordam**: "Sistema" existe nos dois
 * querendo dizer coisas diferentes, então quem aprende um erra no outro.
 *
 * ⚠️ Casa só a raiz e as seções conhecidas. `/admin/configuracoes/geral` (a
 * `AdminConfigPage`) e `/admin/configuracoes/horario-funcionamento` continuam
 * com a casca antiga de propósito: são telas de destino, ainda não migradas, e
 * tirar a navegação delas agora as deixaria sem caminho de volta — o oposto do
 * G-3. Prefixo cru pegaria as duas.
 */
export function isConfiguracoesNova(pathname: string, secoes: ReadonlyArray<string>): boolean {
  if (pathname === '/admin/configuracoes') return true;
  // ⚠️ `([^/]+)$` casava SÓ dois segmentos, e por isso
  // `/admin/configuracoes/catalogos/tags` (três) caía fora: o `AdminLayout`
  // achava que não era a Configurações nova e devolvia a barra de abas antiga
  // POR CIMA da casca.
  //
  // Foi metade do "está amador" que o smoke de 02/08 encontrou — a tela ficava
  // limpa na seção e empilhava três navegações assim que um item abria. A outra
  // metade era a página embutida trazendo o próprio cabeçalho.
  const m = pathname.match(/^\/admin\/configuracoes\/([^/]+)(?:\/[^/]+)?$/);
  return !!m && secoes.includes(m[1]);
}

export function isModuleRoute(pathname: string): boolean {
  // Caso especial PRIMEIRO: /admin/projeto-tipos NÃO é módulo (catálogo)
  if (pathname.startsWith('/admin/projeto-tipos')) return false;
  for (const prefix of MODULE_ROUTE_PREFIXES) {
    // Match exato (sem barra final)
    const normalized = prefix.replace(/\/$/, '');
    if (pathname === normalized) return true;
    // Match com sub-rota
    if (pathname.startsWith(prefix.endsWith('/') ? prefix : `${prefix}/`)) return true;
  }
  return false;
}

// ─── Grupos do menu admin (A5.3 + B2 Lote 2) ──────────────────────────────
// Extraídos pra cá (de AdminLayout.tsx) p/ serem testáveis sem render e sem
// violar a regra react-refresh (componente JSX separado de dados puros).

/**
 * ROTAS-ORFAS (06/08/2026) — `ADMIN_GROUPS`, `resolveActiveGroup` e os tipos
 * `AdminGroup`/`AdminItem` saíram daqui com a barra de abas antiga.
 *
 * Eram 27 links em dois níveis, e a barra era a ÚNICA casa de várias rotas.
 * Derrubá-la exigiu primeiro dar destino a cada uma (as sete decisões do
 * Matheus) — a ordem inversa teria apagado acesso em silêncio, que é o erro já
 * registrado neste arquivo em 2026-08-01: "link de menu é a camada MAIS
 * EXTERNA e tem que cair primeiro, não por último".
 *
 * Sobrevive aqui o que a casca ainda usa: `MODULE_ROUTE_PREFIXES`,
 * `isModuleRoute` e `isConfiguracoesNova` (acima).
 *
 * A prova de que nada ficou inalcançável, rota por rota:
 * `src/pages/admin/__tests__/barra-antiga-nada-inalcancavel.test.ts`.
 */
